import { Component } from '@angular/core';
import { MessageTemplateSample } from '@core/constants/message-flow';
import { FacebookAccount } from '@core/models/interfaces/facebook/facebook-account';
import { FanpageDetails } from '@core/models/interfaces/facebook/facebook-ads';
import { AdAccount, AdAccountWithBusiness } from '@core/models/interfaces/facebook/integration';
import { FlowDetails, PartnerAppDetails } from '@core/models/interfaces/facebook/partner-apps';
import { FacebookAdsAdminService } from '@core/services/facebook.service.ts/facebook-ads-admin.service';
import { FacebookMessageFlowService } from '@core/services/facebook.service.ts/message-flow.service';
import { FacebookPartnerAppService } from '@core/services/facebook.service.ts/partner-apps.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CustomLoadingComponent } from '@shared/components/custom-loading/custom-loading.component';
import { Pagination } from '@shared/components/custom-paginator/custom-paginator.component';
import { AppState } from '@state/app.state';
import { selectFacebookAccountSelected } from '@state/auth/auth.selectors';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { MessageFlowViewComponent } from './components/message-flow-view/message-flow-view.component';
import { PartnerAppsViewComponent } from './components/partner-apps-view/partner-apps-view.component';

@Component({
  selector: 'app-partner-apps',
  standalone: true,
  imports: [AccordionModule, PartnerAppsViewComponent, MessageFlowViewComponent, CustomLoadingComponent],
  templateUrl: './partner-apps.component.html',
  styleUrl: './partner-apps.component.scss'
})
export class PartnerAppsComponent {
  constructor(
    private facebookPartnerAppService: FacebookPartnerAppService,
    private FacebookMessageFlowService: FacebookMessageFlowService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService,
    private store: Store<AppState>,
    private facebookAdsAdminService: FacebookAdsAdminService
  ) {}

  readonly unsubscribe$ = new Subject();

  isInitial: boolean = false;
  isLoading: boolean = false;

  fanPagesByAccount: FanpageDetails[] = [];
  adAccounts: AdAccount[] = [];
  adAccountSelected: AdAccount = {
    id: '',
    name: ''
  };
  fanPageId: string = '';
  fanPageName: string = '';
  currentFanPage: FanpageDetails
  accountBusinesses: AdAccountWithBusiness[] = [];
  facebookAccount: FacebookAccount;
  partnerApps: PartnerAppDetails[] = [];
  currentPartnerAppActive: PartnerAppDetails | undefined;
  partnerAppActive: string = '';
  partnerAppName: string = '';
  isShowMessageFlow: boolean = false;
  isShowNotMessageFlow: boolean = false;
  messageFlowName: string = '';
  pagination: Pagination = {
    page: 0,
    size: 10
  };
  messageFlowActive: FlowDetails;
  keyword: string = '';

  ngOnInit() {
    this.subscribeFacebookAccount();
  }

  onCreatePartnerApps(event: string) {
    const body: { name: string; pageId: string; pageName: string } = {
      name: event,
      pageId: this.fanPageId,
      pageName: this.fanPageName
    };
    this.facebookPartnerAppService
      .createPartnerApp(body)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.onGetPartnerApps();
          this.messageService.add({
            severity: 'success',
            detail: 'Create partner app successfully'
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: 'Create partner app failed'
          });
        }
      });
  }

  subscribeFacebookAccount() {
    this.store
      .select(selectFacebookAccountSelected)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(account => {
        if (account) {
          this.facebookAccount = account;
          this.initData();
        }
      });
  }

  initData() {
    const { accessToken, id } = this.facebookAccount;
    this.facebookAdsAdminService
      .getAdAccountsWithBusiness(accessToken, id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          this.isInitial = true;
          this.accountBusinesses = res.data;

          if (this.accountBusinesses.length) {
            const adAccountStores = sessionStorage.getItem('adAccountInit');
            if (adAccountStores) {
              const adAccountSelected = JSON.parse(adAccountStores);
              if (this.adAccounts.find(item => item.id === adAccountSelected?.id)) {
                this.adAccountSelected = JSON.parse(adAccountStores);
              } else {
                this.adAccountSelected = this.accountBusinesses?.[0].act_account?.[0];
              }
            } else {
              this.adAccountSelected = this.accountBusinesses?.[0].act_account?.[0];
            }
          }
          this.getFanPagesByAccount();
        },
        error: () => {
          this.isInitial = true;
        }
      });
  }

  getFanPagesByAccount() {
    const { accessToken, id } = this.facebookAccount;
    this.facebookAdsAdminService.getFanPage(accessToken, id).subscribe({
      next: res => {
        this.fanPagesByAccount = res.data;
        this.fanPageId = this.fanPagesByAccount?.[0]?.id;
        this.fanPageName = this.fanPagesByAccount?.[0]?.name;
        this.currentFanPage = this.fanPagesByAccount[0]
        this.onGetPartnerApps();
      },
      error: () => {
        this.fanPagesByAccount = [];
      }
    });
  }

  onSearchPartnerApp(event: string) {
    this.pagination.page = 0;
    this.keyword = event;
    this.onGetPartnerApps();
  }

  onDeletePartnerApps(event: { id: string; name: string }) {
    this.confirmationService.confirm({
      header: 'Delete app',
      message: `If you delete the ${event.name} app, all of its message flows will also be deleted.`,
      acceptButtonStyleClass: 'btn-xl btn-danger',
      rejectButtonStyleClass: 'btn-xl btn-outline-secondary',
      rejectLabel: this.translateService.instant('action.cancel'),
      acceptLabel: this.translateService.instant('action.delete'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        this.facebookPartnerAppService
          .deletePartnerApp(event.id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: () => {
              this.onGetPartnerApps();
              this.isShowMessageFlow = false;

              this.messageService.add({
                severity: 'success',
                detail: 'Delete partner app successfully'
              });
            },
            error: error => {
              this.messageService.add({
                severity: 'error',
                detail: error?.error?.error || 'Delete partner app failed'
              });
            }
          });
      }
    });
  }

  onFanPageChange(event: FanpageDetails) {
    this.isShowMessageFlow = false
    this.currentFanPage = event
    this.pagination.page = 0;
    this.fanPageId = event?.id;
    this.fanPageName = event?.name;
    this.onGetPartnerApps();
  }

  onGetPartnerApps(isLazy?: boolean) {
    if (isLazy) {
      this.pagination.page++;
    }

    this.isLoading = true;
    this.facebookPartnerAppService
      .getPartnerApp({
        keyword: this.keyword,
        pageId: this.fanPageId,
        page: this.pagination.page,
        size: this.pagination.size
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          if (isLazy) {
            this.partnerApps = [...this.partnerApps, ...res?.data?.content];
          } else {
            this.partnerApps = res.data.content;
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  onShowMessageFlow(event: FlowDetails) {
    this.messageFlowName = event.name;
    this.isShowMessageFlow = true;
    this.currentPartnerAppActive = undefined;
  }

  onDeleteMessageFlow(event: string) {
    this.confirmationService.confirm({
      header: 'Delete message flow',
      message: `Are you sure you want to delete this ${this.messageFlowName}?`,
      acceptButtonStyleClass: 'btn-xl btn-danger',
      rejectButtonStyleClass: 'btn-xl btn-outline-secondary',
      rejectLabel: this.translateService.instant('action.cancel'),
      acceptLabel: this.translateService.instant('action.delete'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        this.FacebookMessageFlowService.deleteMessageFlow(event)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: () => {
              this.onGetPartnerApps();
              this.isShowMessageFlow = false;
              this.messageService.add({
                severity: 'success',
                detail: 'Delete message flow successfully'
              });
            },
            error: error => {
              this.messageService.add({
                severity: 'error',
                detail: error?.error?.error || 'Delete message flow failed'
              });
            }
          });
      }
    });
  }

  onCreateMessageFlow(partnerAppId: string) {
    const length = this.partnerApps.find(item => item.id === partnerAppId)?.flows?.length;

    const body = {
      name: 'New message flow' + (length && length > 0 ? ` ${length}` : ''),
      json: MessageTemplateSample,
      partnerAppId: partnerAppId
    };
    this.FacebookMessageFlowService.createMessageFlow(body)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.onGetPartnerApps();
          this.isShowNotMessageFlow = false;
          this.messageService.add({
            severity: 'success',
            detail: 'Create message flow successfully'
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: 'Create message flow failed'
          });
        }
      });
  }

  onPartnerAppActiveChange() {
    this.isShowMessageFlow = false;
    this.currentPartnerAppActive = this.partnerApps.find(item => item.id === this.partnerAppActive);
    this.isShowNotMessageFlow = this.currentPartnerAppActive?.flows.length === 0;
  }

  onUpdateMessageFlow(event: { active: FlowDetails; name: string }) {
    const body = {
      name: event.name,
      json: event.active.json,
      partnerAppId: this.partnerAppActive
    };

    this.FacebookMessageFlowService.editMessageFlow(body, event.active.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.onGetPartnerApps();
          this.messageService.add({
            severity: 'success',
            detail: 'Update message flow successfully'
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: 'Update message flow failed'
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
